import type { ProductDetailsDto, ProductVariantDto } from "../../../../api";
import { Text } from "../../../../components/common/typography";

type TemplateProduct = ProductDetailsDto["product"];

interface SpecRow {
  label: string;
  value: string;
}

function nameOf(obj: { name?: string | null } | null | undefined): string | null {
  return obj?.name?.trim() || null;
}

/**
 * Build the static spec rows for a variant + template, skipping any axis key
 * already rendered as a variant selector. Axis keys correspond to the keys in
 * `getVariantAxisValues` (ram, cpu, condition, model, paren_0, paren_1, ...).
 */
function collectSpecs(
  variant: ProductVariantDto,
  template: TemplateProduct | null,
  excludeKeys: ReadonlySet<string>,
): SpecRow[] {
  const rows: SpecRow[] = [];
  const push = (axisKey: string | null, label: string, value: string | null | undefined) => {
    if (axisKey && excludeKeys.has(axisKey)) return;
    if (value && value.trim()) rows.push({ label, value: value.trim() });
  };

  // Template-level (never axis): always show when set.
  push(null, "Brand", nameOf(template?.brand) || nameOf(variant.brand));
  push(null, "Country of origin", nameOf(template?.countryOfOrigin) || nameOf(variant.countryOfOrigin));
  push(null, "Warranty", nameOf(template?.warranty) || nameOf(variant.warranty));
  push(null, "Warranty type", nameOf(template?.warrantyType) || nameOf(variant.warrantyType));
  push(null, "Deal type", nameOf(template?.dealType) || nameOf(variant.dealType));

  // Variant-level: skip when promoted to a selector axis.
  push("model", "Model", variant.model);
  push("condition", "Condition", nameOf(variant.condition));
  push("ram", "Memory (RAM)", null);
  push("cpu", "CPU", null);

  return rows;
}

interface ProductSpecsSectionProps {
  variant: ProductVariantDto;
  template: TemplateProduct | null;
  /** Axis keys currently shown as selectors — those are excluded here. */
  excludeKeys: ReadonlySet<string>;
}

export function ProductSpecsSection({ variant, template, excludeKeys }: ProductSpecsSectionProps) {
  const specs = collectSpecs(variant, template, excludeKeys);
  if (specs.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <Text variant="bodyBold" tone="dark">Specifications</Text>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5">
        {specs.map((row) => (
          <div key={row.label} className="contents">
            <dt>
              <Text variant="bodySm" tone="muted" inline>{row.label}</Text>
            </dt>
            <dd>
              <Text variant="bodySm" tone="dark" inline>{row.value}</Text>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
